import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import logo from "@/assets/logo-kmkit-H.png";
import { useLanguage, Language } from "@/contexts/LanguageContext";

const Header = () => {
  const { language, setLanguage, t, getLocalizedPath } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { path: "", label: t("nav.home") },
    { path: "products", label: t("nav.products") },
    { path: "industries", label: t("nav.industries") },
    { path: "contacts", label: t("nav.contacts") },
  ];

  const languages: Language[] = ["en", "de", "es"];

  const isActive = (path: string) => {
    const currentPath = window.location.pathname;
    const localizedPath = getLocalizedPath(path);
    return currentPath === localizedPath || (path === "" && currentPath === `/${language}`);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          <Link to={getLocalizedPath("")} className="flex items-center">
            <img src={logo} alt="KM KIT" className="h-14" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={getLocalizedPath(link.path)}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  isActive(link.path)
                    ? "text-primary"
                    : "text-foreground/80"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Language & CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="flex items-center space-x-1 border rounded-md">
              {languages.map((lang) => (
                <button
                  key={lang}
                  onClick={() => setLanguage(lang)}
                  className={`px-2 py-1 text-xs font-medium transition-colors ${
                    language === lang
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
            <Button asChild size="sm" className="bg-accent hover:bg-accent/90">
              <Link to={getLocalizedPath("contacts")}>{t("nav.getOffer")}</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-4 border-t">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={getLocalizedPath(link.path)}
                className={`block text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? "text-primary"
                    : "text-foreground/80"
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex items-center space-x-2 pt-2">
              {languages.map((lang) => (
                <button
                  key={lang}
                  onClick={() => {
                    setLanguage(lang);
                    setMobileMenuOpen(false);
                  }}
                  className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                    language === lang
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground"
                  }`}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
            <Button asChild className="w-full bg-accent hover:bg-accent/90">
              <Link to={getLocalizedPath("contacts")} onClick={() => setMobileMenuOpen(false)}>
                {t("nav.getOffer")}
              </Link>
            </Button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
